<template>
    <div>
        <page-title :title="$t('outbox.title')"></page-title>

        <warning-banner class="my-4" v-if="!enabled">
            {{ $t('outbox.disabledHint') }}
        </warning-banner>

        <dl class="mt-5 grid grid-cols-2 gap-5 lg:grid-cols-4">
            <div v-for="tile in tiles" :key="tile.name"
                 class="bg-white px-4 py-5 sm:px-6 shadow rounded-lg overflow-hidden">
                <dt>
                    <p class="text-sm font-medium text-gray-500 truncate">{{ $t('outbox.status.' + tile.name) }}</p>
                </dt>
                <dd>
                    <p :class="tile.name === 'failed' && tile.stat > 0 ? 'text-red-500' : 'text-gray-900'"
                       class="text-2xl font-semibold">
                        {{ tile.stat }}
                    </p>
                </dd>
            </div>
        </dl>

        <div class="flex flex-col sm:flex-row md:mt-0 my-4 sm:space-x-8 items-end">
            <div class="flex flex-row flex-1 w-full mt-4">
                <filter-overlay>
                    <MultiSelect v-model="form.status"
                                 :options="statuses"
                                 class="w-full mt-3"
                                 optionValue="value"
                                 appendTo="body"
                                 :showToggleAll="false"
                                 optionLabel="name"
                                 :placeholder="$t('outbox.statusLabel')">
                        <template #value="slotProps">
                            {{ $t('outbox.statusLabel') }}
                        </template>
                    </MultiSelect>

                    <MultiSelect v-model="form.type"
                                 :options="types"
                                 class="w-full mt-3"
                                 optionValue="value"
                                 appendTo="body"
                                 :showToggleAll="false"
                                 optionLabel="name"
                                 :placeholder="$t('outbox.typeLabel')">
                        <template #value="slotProps">
                            {{ $t('outbox.typeLabel') }}
                        </template>
                    </MultiSelect>

                    <MultiSelect v-model="form.channel"
                                 :options="channels"
                                 class="w-full mt-3"
                                 optionValue="value"
                                 appendTo="body"
                                 :showToggleAll="false"
                                 optionLabel="name"
                                 :placeholder="$t('outbox.channel')">
                        <template #value="slotProps">
                            {{ $t('outbox.channel') }}
                        </template>
                    </MultiSelect>
                </filter-overlay>

                <search-bar class="flex-1"
                            @reset="reset"
                            :placeholder="$t('outbox.search')" v-model="form.search"></search-bar>

                <sort v-model="form.sort" :options="sortOptions"></sort>
            </div>
        </div>

        <div class="mb-4 flex justify-end" v-if="stats.failed > 0">
            <jet-button @click="retryFailed">
                {{ $t('outbox.retryFailed', {amount: stats.failed}) }}
            </jet-button>
        </div>

        <pagination class="my-6" :links="messages.links" prefix="t"></pagination>

        <div class="align-middle min-w-full overflow-x-auto shadow overflow-hidden sm:rounded-lg">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                <tr>
                    <th scope="col" :class="th">{{ $t('outbox.createdAt') }}</th>
                    <th scope="col" :class="th">{{ $t('outbox.orderNumber') }}</th>
                    <th scope="col" :class="th">{{ $t('outbox.channel') }}</th>
                    <th scope="col" :class="th">{{ $t('outbox.typeLabel') }}</th>
                    <th scope="col" :class="th">{{ $t('outbox.positions') }}</th>
                    <th scope="col" :class="th">{{ $t('outbox.statusLabel') }}</th>
                    <th scope="col" :class="th">{{ $t('outbox.attempts') }}</th>
                    <th scope="col" :class="th">{{ $t('outbox.lastError') }}</th>
                    <th scope="col" :class="th"></th>
                </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="message in messages.data" :key="message.id">
                    <td :class="td">{{ message.createdAt }}</td>
                    <td :class="td">
                        <TextLink :href="route('sales.orders.view', message.orderId)">
                            {{ message.orderNumber }}
                        </TextLink>
                    </td>
                    <td :class="td">{{ message.channel }}</td>
                    <td :class="td">{{ $t('outbox.type.' + message.type) }}</td>
                    <td :class="td">
                        <span v-for="position in message.positions" :key="position" class="block">
                            {{ position }}
                        </span>
                    </td>
                    <td :class="td">
                        <status :status="badge(message.status)"></status>
                    </td>
                    <td :class="td">{{ message.attempts }}</td>
                    <td class="px-3 py-4 text-sm text-gray-500 max-w-md truncate" :title="message.lastError">
                        {{ message.lastError }}
                    </td>
                    <td :class="td">
                        <link-button v-if="message.retryable" @click="retry(message)">
                            {{ $t('outbox.retry') }}
                        </link-button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <pagination class="mt-6" :links="messages.links" prefix="b"></pagination>
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import PageTitle from "@/Shared/Components/PageTitle";
import SearchBar from "@/Shared/Components/SearchBar";
import Pagination from "@/Shared/Components/Pagination";
import FilterOverlay from "@/Shared/Components/FilterOverlay";
import Sort from "@/Shared/Components/Sort";
import Status from "@/Shared/Components/Status";
import TextLink from "@/Shared/Components/TextLink";
import WarningBanner from "@/Shared/Components/WarningBanner";
import LinkButton from "@/Jetstream/LinkButton";
import JetButton from "@/Jetstream/PrimaryButton";
import {mapValues, pickBy, throttle} from "lodash";
import {ref} from "vue";
import {i18n} from "@/app";

const {t} = i18n.global;

const sortOptions = [
    {id: 'createdAt', name: t('outbox.createdAt')},
    {id: 'orderNumber', name: t('outbox.orderNumber')},
    {id: 'attempts', name: t('outbox.attempts')},
]

const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    dispatched: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
}

export default {
    components: {
        JetButton,
        LinkButton,
        WarningBanner,
        TextLink,
        Status,
        Sort,
        FilterOverlay,
        Pagination,
        SearchBar,
        PageTitle,
    },
    layout: AppLayout,
    props: ['enabled', 'messages', 'stats', 'filter', 'sort', 'statuses', 'types', 'channels'],
    setup(props) {
        const resetting = ref(false);
        const form = ref({
            reset: null,
            search: props.filter.search,
            status: props.filter.status ? props.filter.status.split('|') : null,
            type: props.filter.type ? props.filter.type.split('|') : null,
            channel: props.filter.channel ? props.filter.channel.split('|') : null,
            sort: props.sort
        })

        return {
            resetting,
            form,
            sortOptions,
            th: 'px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
            td: 'px-3 py-4 whitespace-nowrap text-sm text-gray-500',
        }
    },
    computed: {
        tiles() {
            return Object.keys(this.stats).map((name) => ({name, stat: this.stats[name]}));
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                let query = {
                    "filter[search]": this.form.search,
                    "filter[status]": this.form.status ? this.form.status.join('|') : null,
                    "filter[type]": this.form.type ? this.form.type.join('|') : null,
                    "filter[channel]": this.form.channel ? this.form.channel.join('|') : null,
                    "sort": this.form.sort,
                    "reset": this.resetting ?? null,
                };

                this.resetting = false;

                this.$inertia.get(this.route("sales.outbox.index"), pickBy(query), {
                    only: ['messages', 'stats', 'filter', 'sort'],
                    preserveState: true,
                });
            }, 700)
        }
    },
    methods: {
        badge(status) {
            return {name: this.$t('outbox.status.' + status), color: colors[status]};
        },
        retry(message) {
            this.$inertia.put(this.route('sales.outbox.retry', message.id), {}, {
                onSuccess: () => this.$notify({
                    group: "main",
                    title: 'actions.success',
                    text: 'outbox.retried'
                }, 2000)
            });
        },
        retryFailed() {
            this.$inertia.put(this.route('sales.outbox.retryFailed'), {}, {
                onSuccess: () => this.$notify({
                    group: "main",
                    title: 'actions.success',
                    text: 'outbox.retried'
                }, 2000)
            });
        },
        reset() {
            this.resetting = true;
            this.form = mapValues(this.form, (val, prop) => {
                return prop === 'sort' ? '-createdAt' : null;
            });
        }
    }
}
</script>

<style scoped>

</style>
